import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmailChange } from '@/api/auth/authApi';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';

type VerificationState = 'verifying' | 'confirmed' | 'invalid';

export function VerifyEmailChangeScreen() {
  const { t } = useTranslation('auth');
  const [searchParameters] = useSearchParams();
  const tokenParameter = searchParameters.get('token');
  const resolvedToken = tokenParameter === null ? '' : tokenParameter;
  const [verificationState, setVerificationState] = useState<VerificationState>('verifying');
  const hasStartedVerification = useRef(false);

  useEffect(
    function confirmEmailChange() {
      if (hasStartedVerification.current === true) {
        return;
      }
      hasStartedVerification.current = true;
      if (resolvedToken.length === 0) {
        setVerificationState('invalid');
        return;
      }
      verifyEmailChange(resolvedToken)
        .then(function markConfirmed() {
          setVerificationState('confirmed');
        })
        .catch(function markInvalid() {
          setVerificationState('invalid');
        });
    },
    [resolvedToken]
  );

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-7">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>

          {verificationState === 'verifying' ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <LoadingSpinner />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('verifyEmailChangeScreen.verifyingTitle')}
              </h1>
              <p className="text-sm text-zoomuted mt-2">
                {t('verifyEmailChangeScreen.verifyingSubtitle')}
              </p>
            </div>
          ) : verificationState === 'confirmed' ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-zoosuccessbg flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" className="text-xl text-zoosuccesstext" />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('verifyEmailChangeScreen.successTitle')}
              </h1>
              <p className="text-sm text-zoomuted mt-2">
                {t('verifyEmailChangeScreen.successSubtitle')}
              </p>
              <Button asChild size="large" className="mt-6 w-full">
                <Link to={APPLICATION_ROUTES.login}>
                  {t('verifyEmailChangeScreen.backToLogin')}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-2xl bg-zooerrorbg flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" className="text-xl text-zooerrortext" />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('verifyEmailChangeScreen.invalidTitle')}
              </h1>
              <p className="text-sm text-zoomuted mt-2">
                {t('verifyEmailChangeScreen.invalidDescription')}
              </p>
              <Button asChild size="large" className="mt-6 w-full">
                <Link to={APPLICATION_ROUTES.login}>
                  {t('verifyEmailChangeScreen.backToLogin')}
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
